import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import ProgramForm, { ProgramFormData } from '@/components/programs/ProgramForm';
import { Notification } from '@/components/ui/Notification';
import { createProgram, getErrorMessage, getProgram, Program, updateProgram } from '@/lib/api';
import { DEFAULT_SUPPLIER } from '@/lib/constants/suppliers';

// Helper function to calculate the default start time for new programs
const calculateDefaultSince = (): Date => {
  const now = new Date();
  const minutes = now.getMinutes();
  const seconds = now.getSeconds();
  const milliseconds = now.getMilliseconds();

  // If already on a 15-minute mark with 0 seconds/ms, use current time
  if (minutes % 15 === 0 && seconds === 0 && milliseconds === 0) {
    return now;
  }

  // Calculate minutes to add to reach the next 15-minute interval
  const minutesToAdd = 15 - (minutes % 15);
  const targetDate = new Date(now.getTime() + minutesToAdd * 60 * 1000);

  // Set seconds and milliseconds to 0
  targetDate.setSeconds(0, 0);

  return targetDate;
};

export function ProgramDetail() {
  const { t } = useTranslation(['programs', 'common']);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const idParam = searchParams.get('id');
  const programId = idParam ? parseInt(idParam, 10) : null;

  // Determine if in edit mode
  const isEditMode = location.pathname.includes('/edit');

  const [loading, setLoading] = useState(isEditMode);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [program, setProgram] = useState<Program | null>(null);
  // Initialize state with a function to calculate default 'since' only on mount for create mode
  const [initialFormData, setInitialFormData] = useState<ProgramFormData>(() => {
    const defaultSince = isEditMode ? new Date() : calculateDefaultSince();
    return {
      since: defaultSince,
      duration: '180', // Default 3 minutes
      supplier: DEFAULT_SUPPLIER,
      playerId: undefined,
    };
  });
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  // Fetch program data if in edit mode
  useEffect(() => {
    // In create mode, the default 'since' is already set by useState initializer
    if (!isEditMode) return;

    // Redirect to programs list page if ID is missing in edit mode
    if (!programId) {
      navigate('/programs');
      return;
    }

    const fetchProgram = async () => {
      try {
        setLoading(true);
        const response = await getProgram(programId);
        const programData = response.data;
        setProgram(programData);

        // Set form data based on fetched program data for editing
        // This overwrites the initial default calculated by useState
        setInitialFormData({
          since: programData.since ? new Date(programData.since) : null, // Use fetched data
          duration: programData.duration.toString(),
          supplier: programData.supplier || DEFAULT_SUPPLIER,
          playerId: programData.playerId,
        });

        setError(null);
      } catch (err) {
        console.error('Failed to fetch program:', err);
        setError(getErrorMessage(err));
      } finally {
        setLoading(false);
      }
    };

    fetchProgram();
  }, [programId, navigate, isEditMode]);

  // Close notification
  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false,
    });
  };

  // Form submission handler
  const handleSubmit = async (formData: ProgramFormData) => {
    setSubmitting(true);

    try {
      // Calculate end date/time from start date/time
      const sinceDate = formData.since!;
      const durationInSeconds = parseInt(formData.duration, 10);
      const untilDate = new Date(sinceDate.getTime() + durationInSeconds * 1000);

      const programData = {
        since: sinceDate.toISOString(),
        until: untilDate.toISOString(),
        duration: durationInSeconds,
        supplier: formData.supplier,
        playerId: formData.playerId,
      };

      if (isEditMode && programId) {
        // Edit mode: Update program using API
        await updateProgram(programId, programData);
        setNotification({
          open: true,
          message: t('programs:messages.updateSuccess'),
          severity: 'success',
        });
      } else {
        // Create mode: Create program using API
        await createProgram(programData);
        setNotification({
          open: true,
          message: t('programs:messages.createSuccess'),
          severity: 'success',
        });
      }

    } catch (err) {
      console.error(`Failed to ${isEditMode ? 'update' : 'create'} program:`, err);
      // Display error message
      setNotification({
        open: true,
        message: err instanceof Error ? err.message : getErrorMessage(err),
        severity: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Set page title and button label
  const pageTitle = isEditMode ? t('programs:titles.edit') : t('programs:titles.create');
  const submitLabel = isEditMode ? t('common:buttons.update') : t('common:buttons.create');

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/programs')}
            sx={{ mr: 2 }}
          >
            {t('common:buttons.back')}
          </Button>
          <Typography variant="h4" component="h1">
            {pageTitle}
          </Typography>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box sx={{ my: 4 }}>
          <Alert severity="error">{error}</Alert>
          <Button sx={{ mt: 2 }} variant="contained" onClick={() => navigate('/programs')}>
            {t('programs:buttons.backToList')}
          </Button>
        </Box>
      ) : isEditMode && !program ? (
        <Box sx={{ my: 4 }}>
          <Alert severity="warning">{t('programs:messages.programNotFound')}</Alert>
          <Button sx={{ mt: 2 }} variant="contained" onClick={() => navigate('/programs')}>
            {t('programs:buttons.backToList')}
          </Button>
        </Box>
      ) : (
        <Paper sx={{ p: 3 }}>
          <ProgramForm
            initialData={initialFormData}
            onSubmit={handleSubmit}
            submitLabel={submitLabel}
            loading={submitting}
          />
        </Paper>
      )}

      {/* Notification */}
      <Notification
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={handleCloseNotification}
      />
    </>
  );
}
